// Section 3: households are families of 2 to 5. Couples first, then children to plausible
// parents, then the remaining singles in pairs. Iteration is in id order; choices use the rng.
import type { Config } from '../config';
import { mintId } from './ids';
import type { Rng } from './rng';
import type { GameState, Household, HouseholdId, Person, PersonId } from './types';

function years(cfg: Config, p: Person): number {
  return p.ageSeasons / cfg.people.seasonsPerYear;
}

function newHousehold(state: GameState, members: Person[]): Household {
  const id = mintId(state, 'household');
  const household: Household = {
    id,
    memberIds: members.map((m) => m.id).sort(),
    buildingId: null,
    foundedTurn: 0,
    isNewcomer: false,
  };
  for (const m of members) m.householdId = id;
  state.households[id] = household;
  return household;
}

function join(household: Household, person: Person): void {
  person.householdId = household.id;
  household.memberIds.push(person.id);
  household.memberIds.sort();
}

/** Form households for freshly generated people (mutates `state`, which is still being built). */
export function formHouseholds(cfg: Config, state: GameState, people: Person[], rng: Rng): void {
  const g = cfg.newGame;
  const grownUps = people.filter((p) => p.ageSeasons >= cfg.people.adultAtSeasons);
  const children = people.filter((p) => p.ageSeasons < cfg.people.adultAtSeasons);
  const paired = new Set<PersonId>();
  const couples: { a: Person; b: Person; household: Household }[] = [];

  // 1. Couples: each woman (by id) pairs with the closest-aged unpaired man within the gap.
  for (const woman of grownUps.filter((p) => p.sex === 'female')) {
    if (!rng.happened(rng.chance(g.coupleChance))) continue;
    let best: Person | null = null;
    for (const man of grownUps) {
      if (man.sex !== 'male' || paired.has(man.id)) continue;
      const gap = Math.abs(years(cfg, man) - years(cfg, woman));
      if (gap > g.coupleMaxAgeGapYears) continue;
      if (best === null || gap < Math.abs(years(cfg, best) - years(cfg, woman))) best = man;
    }
    if (best === null) continue;
    paired.add(woman.id).add(best.id);
    woman.partnerId = best.id;
    best.partnerId = woman.id;
    couples.push({ a: woman, b: best, household: newHousehold(state, [woman, best]) });
  }

  // 2. Children, oldest first, to a couple whose mother was of child-bearing age at the birth.
  const [minGap, maxGap] = g.parentAgeAtBirthYears as [number, number];
  const byAgeDesc = children
    .slice()
    .sort((x, y) => y.ageSeasons - x.ageSeasons || (x.id < y.id ? -1 : 1));
  const singles = grownUps.filter((p) => !paired.has(p.id));
  for (const child of byAgeDesc) {
    const fits = couples.filter((c) => {
      const gap = years(cfg, c.a) - years(cfg, child);
      return gap >= minGap && gap <= maxGap && c.household.memberIds.length < g.householdSizeMax;
    });
    if (fits.length > 0) {
      const c = rng.pick(fits);
      join(c.household, child);
      child.parentIds = [c.a.id, c.b.id];
      c.a.childIds.push(child.id);
      c.b.childIds.push(child.id);
      continue;
    }
    // A single parent with a plausible age gap, forming or growing their household.
    const parent = singles.find((s) => {
      const gap = years(cfg, s) - years(cfg, child);
      const room = state.households[s.householdId]?.memberIds.length ?? 1;
      return gap >= minGap && gap <= maxGap + 10 && room < g.householdSizeMax;
    });
    if (parent !== undefined) {
      const existing = state.households[parent.householdId];
      if (existing === undefined) newHousehold(state, [parent, child]);
      else join(existing, child);
      child.parentIds = [parent.id];
      parent.childIds.push(child.id);
      continue;
    }
    // Otherwise the child is taken in by the smallest household with room.
    const host = smallestWithRoom(cfg, state);
    if (host !== null) join(host, child);
    else newHousehold(state, [child]);
  }

  // 3. Remaining singles without a household: pairs, then into the smallest household.
  const loose = singles.filter((s) => state.households[s.householdId] === undefined);
  while (loose.length >= 2) {
    const a = loose.shift() as Person;
    const b = loose.shift() as Person;
    newHousehold(state, [a, b]);
  }
  for (const last of loose) {
    const host = smallestWithRoom(cfg, state);
    if (host !== null) join(host, last);
    else newHousehold(state, [last]);
  }
}

function smallestWithRoom(cfg: Config, state: GameState): Household | null {
  let best: Household | null = null;
  for (const id of Object.keys(state.households).sort()) {
    const h = state.households[id as HouseholdId];
    if (h === undefined || h.memberIds.length >= cfg.newGame.householdSizeMax) continue;
    if (best === null || h.memberIds.length < best.memberIds.length) best = h;
  }
  return best;
}
